import { useState } from "react";
import { toast } from "react-hot-toast";

import { createAxiosInstance } from "api/axiosConfig";
import { Button } from "components/ui/Button";
import { PENDING_IB_REFERRAL_KEY } from "features/referrals/constants";

import s from "./AdditionalInformationsForm.module.css";

// Also used by the eLab lab-ordering flow, which hands the GP over to the same
// Healthlink Integrated Browser. Everything it needs is additive and defaulted, so
// the referral behaviour is unchanged when the new props are absent: `title`,
// `subtitle` and `confirmationLabel` carry the wording (a lab order must not say
// "referral"), and `onFinish` replaces the referral check-status call with the
// caller's own verification — a lab order is confirmed by a different endpoint.
export const AdditionalInformationsForm = ({
  link,
  referralId,
  onContinue,
  onAutoRedirect,
  hideFinishButton,
  showDraftNotice,
  title = "Complete Referral in Healthlink",
  subtitle = "Complete the referral form on Healthlink to finish.",
  confirmationLabel = "I have finished the submission of the referral at Healthlink.",
  redirectLabel = "Continue on Healthlink (auto redirect)",
  redirectNote = "Use the copy-link option if auto redirect fails.",
  // Replaces the how-it-works paragraph. The referral wording describes a redirect
  // that brings the GP back automatically; Healthlink's lab order page never does
  // that, so a caller whose flow differs must be able to say what really happens.
  instruction,
  // Drops this component's own card chrome for callers that already render it inside
  // a bordered panel — otherwise the two borders read as one broken frame.
  isFlush = false,
  onFinish,
}) => {
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [copyStatus, setCopyStatus] = useState("Copy Link");
  const [isChecking, setIsChecking] = useState(false);

  const tenantVal = localStorage.getItem("kinesin-tenant");

  const handleCopy = async () => {
    if (!link) return;

    try {
      await navigator.clipboard.writeText(link);
      setCopyStatus("Copied!");
      setTimeout(() => setCopyStatus("Copy Link"), 3000);
    } catch {
      setCopyStatus("Select manually");
    }
  };

  // "Auto redirect" path — navigate the whole page to Healthlink carrying a
  // redirectUrl back to this referral. This sends a cross-origin Referer, so HL
  // may strip the params (→ "session expired"); it's the spare option we expect
  // to consolidate away. The manual "Open form" path below has no Referer and is
  // the reliable one. Kept side-by-side until we decide which to keep.
  const handleAutoRedirect = () => {
    if (!link) return;

    const returnUrl = new URL(window.location.href);

    if (referralId) {
      returnUrl.searchParams.set("referralId", String(referralId));
      // HealthLink drops our redirectUrl params on the way back — stash the id
      // so the return handler can confirm it regardless of what HL appends.
      sessionStorage.setItem(PENDING_IB_REFERRAL_KEY, String(referralId));
    }

    window.location.href = `${link}&redirectUrl=${encodeURIComponent(
      returnUrl.toString()
    )}`;
  };

  const handleFinish = async () => {
    // A caller that verifies for itself (eLab checks whether Healthlink has the
    // completed order) owns the outcome: it reports its own failure and we only
    // advance on a confirmed success.
    if (onFinish) {
      setIsChecking(true);

      try {
        const isConfirmed = await onFinish();

        if (isConfirmed) onContinue();
      } finally {
        setIsChecking(false);
      }

      return;
    }

    if (!referralId) {
      onContinue();
      return;
    }

    setIsChecking(true);

    try {
      const axios = createAxiosInstance();
      const response = await axios.post(
        `${tenantVal}/healthlink/referrals/${referralId}/check-status`
      );

      if (response.data.confirmed) {
        onContinue();
      } else {
        toast.error(
          "Healthlink has not confirmed receipt yet. Please ensure you have submitted the form in the new tab, or try again in a few seconds."
        );
      }
    } catch (error) {
      console.error("Failed to check status", error);
      toast.error(
        "Healthlink hasn't confirmed the referral yet. Wait a few seconds and click Finish again.",
        { duration: 6000 }
      );
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className={`${s.card} ${isFlush ? s.cardFlush : ""}`}>
      <div className={s.header}>
        <h3 className={s.title}>{title}</h3>
        <p className={s.subtitle}>{subtitle}</p>
      </div>

      {!!showDraftNotice && (
        <p className={`${s.stepInstruction} ${s.draftNote}`}>
          If it can’t be confirmed, the referral is saved as a draft and stays
          available in this list, so you can come back and finish it later.
        </p>
      )}

      <div className={s.options}>
        {/* Auto redirect — the "spare" path (sends a Referer; HL may strip
            params). Kept until we consolidate on one flow. The eLab flow passes its
            own handler and wording, because Healthlink's lab order page refuses a
            redirectUrl outright and it opens in a new tab instead. */}
        <button
          onClick={onAutoRedirect ?? handleAutoRedirect}
          className={s.redirectButton}
        >
          {redirectLabel}
        </button>
        <p className={s.fallbackNote}>{redirectNote}</p>
      </div>

      <div className={s.linkBox}>
        {/* Manual — copy the link and paste it into a new tab. Pasting sends no
            Referer, so HL keeps the query params (incl. SessionID). */}
        <p className={s.stepLabel}>Or copy the link and open it in a new tab</p>
        <div className={s.linkRow}>
          <input readOnly value={link} className={s.linkInput} />
          <Button variant="outline" size="small" onClick={handleCopy}>
            {copyStatus}
          </Button>
        </div>
      </div>

      <p className={s.stepInstruction}>
        {instruction ?? (
          <>
            There are two ways to complete this referral.{" "}
            <strong>Auto redirect</strong> takes you to Healthlink and brings you
            back here automatically when you are done. Alternatively,{" "}
            <strong>copy the link</strong> above and paste it into a new browser
            tab, complete the form there, then return here and click{" "}
            <strong>Finish</strong>.
          </>
        )}
      </p>

      {!hideFinishButton && (
        <>
          <div className={s.divider} />
          <div className={s.footer}>
            <label className={s.checkboxLabel}>
              <input
                type="checkbox"
                className={s.checkbox}
                checked={isConfirmed}
                onChange={(e) => setIsConfirmed(e.target.checked)}
                disabled={isChecking}
              />
              {confirmationLabel}
            </label>
            <button
              className={s.finishButton}
              onClick={handleFinish}
              disabled={!isConfirmed || isChecking}
            >
              {isChecking ? "Verifying..." : "Finish"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
